import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Search, Filter, Download, Eye, Send, Trash2,
  Clock, CheckCircle, XCircle, AlertTriangle, PenTool,
  MoreVertical, ChevronRight, Calendar, User, Briefcase,
  RefreshCw, X, Loader2, FileDown, Mail, History,
  FileSearch, Sparkles, MessageSquare
} from 'lucide-react';
import { AIAssistant, useAIAssistant } from '../components/AIAssistant';
import { AIDocumentAnalyzer } from '../components/AIDocumentAnalyzer';
import { AIFloatingButton } from '../components/AIFloatingButton';
import { CommandPalette, useCommandPalette } from '../components/CommandPalette';

interface Document {
  id: string;
  name: string;
  templateName: string;
  templateId: string;
  clientId: string;
  clientName: string;
  matterId: string;
  matterNumber: string;
  matterName: string;
  status: DocumentStatus;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  version: number;
  content?: string;
  outputs: {
    pdf?: string;
    docx?: string;
  };
  signatureStatus?: {
    envelopeId: string;
    status: 'pending' | 'sent' | 'partial' | 'complete' | 'declined';
    signerCount: number;
    signedCount: number;
  };
}

type DocumentStatus = 
  | 'draft'
  | 'pending_review'
  | 'reviewed'
  | 'approved'
  | 'sent_for_signature'
  | 'partially_signed'
  | 'fully_signed'
  | 'recorded'
  | 'filed'
  | 'complete'
  | 'voided';

const STATUS_CONFIG: Record<DocumentStatus, { label: string; color: string; icon: any }> = {
  draft: { label: 'Draft', color: 'gray', icon: FileText },
  pending_review: { label: 'Pending Review', color: 'yellow', icon: Clock },
  reviewed: { label: 'Reviewed', color: 'blue', icon: Eye },
  approved: { label: 'Approved', color: 'green', icon: CheckCircle },
  sent_for_signature: { label: 'Sent for Signature', color: 'purple', icon: PenTool },
  partially_signed: { label: 'Partially Signed', color: 'orange', icon: PenTool },
  fully_signed: { label: 'Fully Signed', color: 'green', icon: CheckCircle },
  recorded: { label: 'Recorded', color: 'blue', icon: FileText },
  filed: { label: 'Filed', color: 'blue', icon: FileText },
  complete: { label: 'Complete', color: 'green', icon: CheckCircle },
  voided: { label: 'Voided', color: 'red', icon: XCircle },
};

export function DocumentList() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showActions, setShowActions] = useState<string | null>(null);

  // AI Feature States
  const aiAssistant = useAIAssistant();
  const commandPalette = useCommandPalette();
  const [showDocumentAnalyzer, setShowDocumentAnalyzer] = useState(false);
  const [analyzingDocument, setAnalyzingDocument] = useState<Document | null>(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));

    setDocuments([
      {
        id: '1',
        name: 'Smith Property - Warranty Deed',
        templateName: 'Wisconsin Warranty Deed',
        templateId: 'tmpl-1',
        clientId: 'client-1',
        clientName: 'John Smith',
        matterId: 'matter-1',
        matterNumber: '2024-0089-REP',
        matterName: 'Smith Property Purchase',
        status: 'sent_for_signature',
        createdAt: new Date('2024-11-28'),
        updatedAt: new Date('2024-11-30'),
        createdBy: 'Seth Goettelman',
        version: 1,
        content: 'WARRANTY DEED\n\nThis Warranty Deed is made this 28th day of November, 2024, by and between John Smith (Grantor) and Jane Doe (Grantee).\n\nThe Grantor hereby conveys and warrants to the Grantee the following described property located in Door County, Wisconsin:\n\nLot 15, Block 3, Sunset Shores Subdivision...',
        outputs: { pdf: '#', docx: '#' },
        signatureStatus: {
          envelopeId: 'env-1',
          status: 'partial',
          signerCount: 3,
          signedCount: 1,
        },
      },
      {
        id: '2',
        name: 'Johnson Trust - Operating Agreement',
        templateName: 'LLC Operating Agreement',
        templateId: 'tmpl-2',
        clientId: 'client-2',
        clientName: 'Johnson Family Trust',
        matterId: 'matter-2',
        matterNumber: '2024-0085-EPT',
        matterName: 'Johnson Family Trust',
        status: 'complete',
        createdAt: new Date('2024-10-15'),
        updatedAt: new Date('2024-10-20'),
        createdBy: 'Seth Goettelman',
        version: 2,
        content: 'OPERATING AGREEMENT\n\nThis Operating Agreement is entered into as of October 15, 2024, by and among the Members listed herein.\n\nARTICLE I - FORMATION\nThe Company is a limited liability company organized pursuant to the Wisconsin Limited Liability Company Act...',
        outputs: { pdf: '#', docx: '#' },
      },
      {
        id: '3',
        name: 'ABC Holdings - Articles of Organization',
        templateName: 'Wisconsin Articles of Organization',
        templateId: 'tmpl-3',
        clientId: 'client-3',
        clientName: 'ABC Holdings LLC',
        matterId: 'matter-3',
        matterNumber: '2024-0092-BUF',
        matterName: 'ABC Holdings LLC Formation',
        status: 'approved',
        createdAt: new Date('2024-12-01'),
        updatedAt: new Date('2024-12-03'),
        createdBy: 'Seth Goettelman',
        version: 1,
        content: 'ARTICLES OF ORGANIZATION\n\nPursuant to Chapter 183 of the Wisconsin Statutes, the undersigned organizer hereby forms a limited liability company as follows:\n\nARTICLE 1 - NAME: ABC Holdings LLC\nARTICLE 2 - REGISTERED AGENT: Seth Goettelman...',
        outputs: { pdf: '#', docx: '#' },
      },
      {
        id: '4',
        name: 'Doe Estate - Simple Will',
        templateName: 'Simple Will',
        templateId: 'tmpl-4',
        clientId: 'client-4',
        clientName: 'Jane Doe',
        matterId: 'matter-4',
        matterNumber: '2024-0078-EPW',
        matterName: 'Doe Estate Planning',
        status: 'draft',
        createdAt: new Date('2024-12-04'),
        updatedAt: new Date('2024-12-04'),
        createdBy: 'Seth Goettelman',
        version: 1,
        content: 'LAST WILL AND TESTAMENT OF JANE DOE\n\nI, Jane Doe, a resident of Door County, Wisconsin, being of sound mind and memory, do hereby make, publish and declare this to be my Last Will and Testament...',
        outputs: { docx: '#' },
      },
      {
        id: '5',
        name: 'Miller Property - Quitclaim Deed',
        templateName: 'Wisconsin Quitclaim Deed',
        templateId: 'tmpl-5',
        clientId: 'client-5',
        clientName: 'Robert Miller',
        matterId: 'matter-5',
        matterNumber: '2024-0095-RES',
        matterName: 'Miller Property Sale',
        status: 'pending_review',
        createdAt: new Date('2024-12-05'),
        updatedAt: new Date('2024-12-05'),
        createdBy: 'Seth Goettelman',
        version: 1,
        content: 'QUITCLAIM DEED\n\nThis Quitclaim Deed is made this 5th day of December, 2024, by Robert Miller (Grantor) to Sarah Miller (Grantee).\n\nThe Grantor hereby quitclaims to the Grantee all interest the Grantor may have in the following described property...',
        outputs: { pdf: '#', docx: '#' },
      },
    ]);
    setLoading(false);
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = 
      doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.matterNumber.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusConfig = (status: DocumentStatus) => STATUS_CONFIG[status];

  const getStatusColorClass = (color: string) => {
    const colors: Record<string, string> = {
      gray: 'bg-gray-500/20 text-gray-400',
      yellow: 'bg-yellow-500/20 text-yellow-400',
      blue: 'bg-blue-500/20 text-blue-400',
      green: 'bg-green-500/20 text-green-400',
      purple: 'bg-purple-500/20 text-purple-400',
      orange: 'bg-orange-500/20 text-orange-400',
      red: 'bg-red-500/20 text-red-400',
    };
    return colors[color] || colors.gray;
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleStatusChange = async (docId: string, newStatus: DocumentStatus) => {
    setDocuments(docs => docs.map(d => 
      d.id === docId ? { ...d, status: newStatus, updatedAt: new Date() } : d
    ));
    setShowActions(null);
  };

  const handleAnalyzeDocument = (doc: Document) => {
    setAnalyzingDocument(doc);
    setShowDocumentAnalyzer(true);
    setShowActions(null);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileText className="w-7 h-7 text-accent-400" />
            Documents
          </h1>
          <p className="text-primary-400 mt-1">
            {documents.length} total documents
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setAnalyzingDocument(null);
              setShowDocumentAnalyzer(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-cyan-500/25"
          >
            <FileSearch className="w-5 h-5" />
            AI Analyze
          </button>
          <button
            onClick={loadDocuments}
            className="flex items-center gap-2 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-400" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-primary-800 border border-primary-600 text-white rounded-lg focus:outline-none focus:border-accent-500"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-primary-800 border border-primary-600 text-white rounded-lg"
        >
          <option value="all">All Status</option>
          {Object.entries(STATUS_CONFIG).map(([value, config]) => (
            <option key={value} value={value}>{config.label}</option>
          ))}
        </select>
      </div>

      {/* Document List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-accent-400 animate-spin" />
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-primary-400">
          <FileText className="w-16 h-16 mb-4 opacity-30" />
          <p className="text-lg">No documents found</p>
          <p className="text-sm mt-1">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredDocuments.map((doc, idx) => {
            const statusConfig = getStatusConfig(doc.status);
            const StatusIcon = statusConfig.icon;
            
            return (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-primary-800/50 border border-primary-700 rounded-xl p-4 hover:border-accent-500/50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getStatusColorClass(statusConfig.color)}`}>
                    <StatusIcon className="w-6 h-6" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-semibold text-white">{doc.name}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusColorClass(statusConfig.color)}`}>
                        {statusConfig.label}
                      </span>
                      {doc.version > 1 && (
                        <span className="text-xs text-primary-500">v{doc.version}</span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 mt-2 text-sm text-primary-400 flex-wrap">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {doc.clientName}
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        {doc.matterNumber}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(doc.createdAt)}
                      </span>
                    </div>

                    {/* Signature Status */}
                    {doc.signatureStatus && (
                      <div className="flex items-center gap-2 mt-2">
                        <PenTool className="w-4 h-4 text-purple-400" />
                        <div className="flex-1 h-2 bg-primary-700 rounded-full overflow-hidden max-w-[150px]">
                          <div 
                            className="h-full bg-purple-500 transition-all"
                            style={{ width: `${(doc.signatureStatus.signedCount / doc.signatureStatus.signerCount) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-primary-400">
                          {doc.signatureStatus.signedCount}/{doc.signatureStatus.signerCount} signed
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {/* AI Analyze Button */}
                    <button 
                      className="p-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 rounded-lg transition-colors"
                      title="AI Analyze"
                      onClick={() => handleAnalyzeDocument(doc)}
                    >
                      <FileSearch className="w-5 h-5" />
                    </button>
                    {doc.outputs.pdf && (
                      <button 
                        className="p-2 text-primary-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        title="Download PDF"
                      >
                        <FileDown className="w-5 h-5" />
                      </button>
                    )}
                    <button 
                      className="p-2 text-primary-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                      title="View"
                      onClick={() => setSelectedDocument(doc)}
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                    <div className="relative">
                      <button 
                        className="p-2 text-primary-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        onClick={() => setShowActions(showActions === doc.id ? null : doc.id)}
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                      
                      {/* Actions Dropdown */}
                      <AnimatePresence>
                        {showActions === doc.id && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="absolute right-0 top-full mt-1 w-48 bg-primary-800 border border-primary-600 rounded-lg shadow-xl z-10 py-1"
                          >
                            <button 
                              onClick={() => handleAnalyzeDocument(doc)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-cyan-400 hover:bg-white/5"
                            >
                              <FileSearch className="w-4 h-4" />
                              AI Analyze
                            </button>
                            <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary-300 hover:bg-white/5">
                              <Eye className="w-4 h-4" />
                              View Details
                            </button>
                            <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary-300 hover:bg-white/5">
                              <Download className="w-4 h-4" />
                              Download
                            </button>
                            <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary-300 hover:bg-white/5">
                              <Mail className="w-4 h-4" />
                              Send to Client
                            </button>
                            {doc.status === 'approved' && (
                              <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary-300 hover:bg-white/5">
                                <PenTool className="w-4 h-4" />
                                Send for Signature
                              </button>
                            )}
                            <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary-300 hover:bg-white/5">
                              <History className="w-4 h-4" />
                              Version History
                            </button>
                            <hr className="my-1 border-primary-700" />
                            <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-white/5">
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Document Detail Modal */}
      <AnimatePresence>
        {selectedDocument && (
          <DocumentDetailModal
            document={selectedDocument}
            onClose={() => setSelectedDocument(null)}
            onStatusChange={handleStatusChange}
            onAnalyze={handleAnalyzeDocument}
          />
        )}
      </AnimatePresence>

      {/* AI Floating Button */}
      <AIFloatingButton
        onOpenChat={() => aiAssistant.open({ currentPage: 'Documents' })}
        onOpenAnalyzer={() => setShowDocumentAnalyzer(true)}
        showAnalyzer={true}
        showSmartFill={false}
        showClauseSuggestions={false}
      />

      {/* Command Palette */}
      <CommandPalette 
        isOpen={commandPalette.isOpen} 
        onClose={commandPalette.close}
        onOpenAIAssistant={() => aiAssistant.open({ currentPage: 'Documents' })}
        onOpenDocumentAnalyzer={() => setShowDocumentAnalyzer(true)}
      />

      {/* AI Assistant Panel */}
      <AIAssistant 
        isOpen={aiAssistant.isOpen} 
        onClose={aiAssistant.close}
        context={aiAssistant.context}
      />

      {/* AI Document Analyzer Modal */}
      <AIDocumentAnalyzer
        isOpen={showDocumentAnalyzer}
        onClose={() => {
          setShowDocumentAnalyzer(false);
          setAnalyzingDocument(null);
        }}
        documentContent={analyzingDocument?.content || 'Select a document to analyze or paste content here.'}
        documentType={analyzingDocument?.templateName || 'Legal Document'}
      />
    </div>
  );
}

// Document Detail Modal
function DocumentDetailModal({ 
  document, 
  onClose,
  onStatusChange,
  onAnalyze,
}: { 
  document: Document; 
  onClose: () => void;
  onStatusChange: (docId: string, status: DocumentStatus) => void;
  onAnalyze: (doc: Document) => void;
}) {
  const statusConfig = STATUS_CONFIG[document.status];

  const getNextStatuses = (current: DocumentStatus): DocumentStatus[] => {
    const transitions: Record<DocumentStatus, DocumentStatus[]> = {
      draft: ['pending_review'],
      pending_review: ['reviewed', 'draft'],
      reviewed: ['approved', 'pending_review'],
      approved: ['sent_for_signature', 'reviewed'],
      sent_for_signature: ['partially_signed', 'fully_signed', 'voided'],
      partially_signed: ['fully_signed', 'voided'],
      fully_signed: ['recorded', 'filed', 'complete'],
      recorded: ['filed', 'complete'],
      filed: ['complete'],
      complete: [],
      voided: [],
    };
    return transitions[current] || [];
  };

  const nextStatuses = getNextStatuses(document.status);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-3xl max-h-[90vh] bg-primary-900 rounded-xl border border-primary-700 flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-primary-700">
          <div>
            <h2 className="text-xl font-semibold text-white">{document.name}</h2>
            <p className="text-sm text-primary-400 mt-1">
              {document.templateName} • Version {document.version}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => onAnalyze(document)}
              className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors"
            >
              <FileSearch className="w-4 h-4" />
              AI Analyze
            </button>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
              <X className="w-5 h-5 text-primary-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                statusConfig.color === 'green' ? 'bg-green-500/20 text-green-400' :
                statusConfig.color === 'yellow' ? 'bg-yellow-500/20 text-yellow-400' :
                statusConfig.color === 'red' ? 'bg-red-500/20 text-red-400' :
                statusConfig.color === 'purple' ? 'bg-purple-500/20 text-purple-400' :
                'bg-gray-500/20 text-gray-400'
              }`}>
                {statusConfig.label}
              </span>
            </div>
            
            {nextStatuses.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-primary-400">Move to:</span>
                {nextStatuses.map(status => (
                  <button
                    key={status}
                    onClick={() => onStatusChange(document.id, status)}
                    className="px-3 py-1 text-sm bg-primary-700 text-primary-200 rounded hover:bg-primary-600 transition-colors"
                  >
                    {STATUS_CONFIG[status].label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-primary-800/50 rounded-lg p-4">
              <p className="text-sm text-primary-400">Client</p>
              <p className="text-white font-medium">{document.clientName}</p>
            </div>
            <div className="bg-primary-800/50 rounded-lg p-4">
              <p className="text-sm text-primary-400">Matter</p>
              <p className="text-white font-medium">{document.matterNumber}</p>
              <p className="text-sm text-primary-400">{document.matterName}</p>
            </div>
            <div className="bg-primary-800/50 rounded-lg p-4">
              <p className="text-sm text-primary-400">Created</p>
              <p className="text-white font-medium">
                {document.createdAt.toLocaleDateString()}
              </p>
              <p className="text-sm text-primary-400">by {document.createdBy}</p>
            </div>
            <div className="bg-primary-800/50 rounded-lg p-4">
              <p className="text-sm text-primary-400">Last Updated</p>
              <p className="text-white font-medium">
                {document.updatedAt.toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Signature Status */}
          {document.signatureStatus && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
              <h3 className="font-medium text-white flex items-center gap-2 mb-3">
                <PenTool className="w-5 h-5 text-purple-400" />
                E-Signature Status
              </h3>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="h-3 bg-primary-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500"
                      style={{ width: `${(document.signatureStatus.signedCount / document.signatureStatus.signerCount) * 100}%` }}
                    />
                  </div>
                </div>
                <span className="text-sm text-primary-300">
                  {document.signatureStatus.signedCount} of {document.signatureStatus.signerCount} signed
                </span>
              </div>
            </div>
          )}

          {/* Activity Timeline */}
          <div>
            <h3 className="font-medium text-white mb-3">Activity</h3>
            <div className="space-y-3">
              {[
                { action: 'Document created', date: document.createdAt, user: document.createdBy },
                { action: 'Status changed to ' + statusConfig.label, date: document.updatedAt, user: document.createdBy },
              ].map((activity, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-accent-400 mt-2" />
                  <div>
                    <p className="text-sm text-white">{activity.action}</p>
                    <p className="text-xs text-primary-400">
                      {activity.date.toLocaleString()} • {activity.user}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 py-4 bg-primary-800/30 border-t border-primary-700">
          {document.outputs.pdf && (
            <button className="flex items-center gap-2 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-600">
              <Download className="w-4 h-4" />
              Download PDF
            </button>
          )}
          {document.outputs.docx && (
            <button className="flex items-center gap-2 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-600">
              <Download className="w-4 h-4" />
              Download DOCX
            </button>
          )}
          <button className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 ml-auto">
            <Mail className="w-4 h-4" />
            Send to Client
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
